import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { CustomerOrderDispatchReportProxyService } from '../../../proxies/inventory/customer-order-dispatch-report.proxy.service';
import { CustomerOrderDispatchReportForm } from '../../../models/order/customer-order-dispatch-report';
import { WorkOrderSearchCommand } from '../../../models/order/work-order';

function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const from = control.get('createdFrom')?.value;
    const to = control.get('createdTo')?.value;
    if (from && to && from > to) {
      return { invalidDateRange: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-customer-order-dispatch-report',
  templateUrl: './customer-order-dispatch-report.component.html',
  styleUrl: './customer-order-dispatch-report.component.scss',
})
export class CustomerOrderDispatchReportComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly reportProxyService = inject(CustomerOrderDispatchReportProxyService);

  private readonly destroy$ = new Subject<void>();

  readonly loading = signal<boolean>(false);
  readonly filteredCustomers = signal<Business[]>([]);
  readonly noResults = signal<boolean>(false);

  readonly reportForm = new FormGroup<CustomerOrderDispatchReportForm>(
    {
      customer: new FormControl<Business | null>(null, [Validators.required]),
      createdFrom: new FormControl<Date | null>(null),
      createdTo: new FormControl<Date | null>(null),
      detailed: new FormControl<boolean>(false, { nonNullable: true }),
    },
    { validators: dateRangeValidator() },
  );

  ngOnInit(): void {
    this.reportForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.noResults()) {
        this.noResults.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filterCustomers(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim() ?? '';
    if (query.length < 2) {
      this.filteredCustomers.set([]);
      return;
    }
    const command: BusinessSearchCommand = {
      name: query,
      code: query,
      businessTypes: [BusinessEntityType.Client],
    };
    this.businessProxyService
      .search(command, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.filteredCustomers.set(response.data?.items ?? []),
        error: () => this.filteredCustomers.set([]),
      });
  }

  generateReport(): void {
    this.reportForm.markAllAsTouched();
    if (!this.validateForm()) {
      return;
    }

    const value = this.reportForm.getRawValue();
    const filters: WorkOrderSearchCommand = {
      customerReference: value.customer!.id,
      createdFrom: value.createdFrom ?? undefined,
      createdTo: value.createdTo ?? undefined,
    };

    this.noResults.set(false);
    this.loading.set(true);

    this.reportProxyService
      .generateWorkOrderDispatchReport(filters, value.detailed)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: ({ blob, workOrderCount }) => {
          if (workOrderCount === 0) {
            this.noResults.set(true);
            this.messageService.add({
              key: 'main',
              severity: 'info',
              summary: this.tr('CustomerOrderReport_NoResults_Title', 'No results'),
              detail: this.tr('CustomerOrderReport_NoResults_Detail', 'No work orders matched the selected filters.'),
            });
          }
          this.downloadBlob(blob, this.buildFilename(value.customer!.name));
        },
      });
  }

  private validateForm(): boolean {
    if (this.reportForm.invalid) {
      if (this.reportForm.hasError('invalidDateRange')) {
        this.messageService.add({
          key: 'main',
          severity: 'warn',
          summary: this.tr('Common_Validation_Error', 'Validation error'),
          detail: this.tr('CustomerOrderReport_Invalid_Date_Range', 'The "from" date must be on or before the "to" date.'),
        });
      }
      return false;
    }
    return true;
  }

  private buildFilename(customerName: string): string {
    const safeName = (customerName ?? 'customer').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
    const date = new Date().toISOString().slice(0, 10);
    return `Customer-Order-Dispatch-Report_${safeName}_${date}.xlsx`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  }

  private tr(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
